import { useEffect, useRef, useState } from "react";
import { T } from "@/components/T";
import { useLang } from "@/context/LanguageContext";
import { fetchAudioLibrary } from "@/lib/api";
import { useAudioPlayer } from "@/context/AudioPlayerContext";
import { ytId } from "@/components/AudioMedia";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { Play, Repeat, Youtube, AudioLines, Radio, ChevronLeft, ChevronRight } from "lucide-react";

const ACCENTS = ["#C75000", "#B8860B", "#2E7D32", "#1F5C9C", "#7E57C2", "#B3241C", "#00897B", "#C77D3A"];
const accentFor = (s = "") => ACCENTS[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % ACCENTS.length];
const TYPE_LABEL = { mantra: "Mantra", stotram: "Stotram", katha: "Katha", namasmarna: "Namasmarana", aarati: "Aarati", bhajan: "Bhajan", vedam: "Vedam", pravachan: "Pravachan" };

function TrackCard({ a, pick }) {
  const { play, track, playing } = useAudioPlayer();
  const yid = a.kind === "youtube" ? ytId(a.url) : "";
  const thumb = a.image || (yid ? `https://img.youtube.com/vi/${yid}/hqdefault.jpg` : "");
  const accent = accentFor(a.category || a.title);
  const title = pick(a.title_hi || a.title, a.title);
  const active = track && track.id === a.id;

  return (
    <button data-testid={`audio-${a.id}`} onClick={() => play({ ...a, title })}
      className={`group relative flex w-44 shrink-0 snap-start flex-col overflow-hidden rounded-xl border bg-surface text-left transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:w-48 ${active ? "border-[var(--gold)] ring-2 ring-[var(--gold)]/40" : "border-line hover:border-[var(--gold)]"}`}>
      <div className="relative aspect-square w-full overflow-hidden">
        {thumb ? (
          <img src={thumb} alt={title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}><AudioLines className="h-10 w-10 text-white/70" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
        {a.atype && <span className="absolute left-2 top-2 rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur">{TYPE_LABEL[a.atype] || a.atype}</span>}
        {a.loop && <span className="absolute right-2 top-2 inline-flex items-center gap-0.5 rounded-full bg-[var(--gold)] px-1.5 py-0.5 text-[9px] font-semibold text-ink"><Repeat className="h-2.5 w-2.5" /></span>}
        <span className="absolute bottom-2 right-2 flex h-11 w-11 translate-y-1 items-center justify-center rounded-full bg-[var(--saffron)] text-white opacity-0 shadow-lg transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
          <Play className="h-5 w-5" fill="currentColor" />
        </span>
        {active && playing && <span className="absolute bottom-2 left-2 rounded-full bg-[var(--gold)] px-2 py-0.5 text-[9px] font-semibold text-ink">{pick("चल रहा", "Playing")}</span>}
      </div>
      <div className="p-3">
        <p className="truncate font-serif-latin text-sm text-ink">{title}</p>
        {a.kind === "youtube" ? <p className="mt-0.5 flex items-center gap-1 text-[11px] text-ink-3"><Youtube className="h-3 w-3" /> {a.deity || a.category || "Audio"}</p>
          : <p className="mt-0.5 flex items-center gap-1 text-[11px] text-ink-3"><AudioLines className="h-3 w-3" /> {a.deity || a.category || "Audio"}</p>}
      </div>
    </button>
  );
}

function Row({ title, subtitle, children }) {
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  return (
    <section className="mt-10">
      <div className="mb-3 flex items-end justify-between gap-3 px-1">
        <div>
          <h2 className="font-serif-latin text-xl text-ink md:text-2xl">{title}</h2>
          {subtitle && <p className="text-sm text-ink-3">{subtitle}</p>}
        </div>
        <div className="hidden gap-1.5 md:flex">
          <button onClick={() => scroll(-1)} className="flex h-8 w-8 items-center justify-center rounded-full border border-line text-ink-2 hover:border-saffron hover:text-saffron"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => scroll(1)} className="flex h-8 w-8 items-center justify-center rounded-full border border-line text-ink-2 hover:border-saffron hover:text-saffron"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div ref={ref} className="flex snap-x gap-4 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {children}
      </div>
    </section>
  );
}

export default function CuratedAudios() {
  const { pick } = useLang();
  const { playChannel, channel: liveChannel } = useAudioPlayer();
  const [data, setData] = useState(null);
  useEffect(() => { fetchAudioLibrary().then(setData).catch(() => setData({ sections: [], channels: [] })); }, []);

  const sections = (data?.sections || []).filter((s) => s.tracks?.length);
  const channels = data?.channels || [];
  const playableChannels = channels.filter((c) => (c.tracks || []).length);

  return (
    <div data-testid="audios-page">
      <Seo path="/audios" title="Sacred Audio Library — Dharmartha" description="Listen to mantras, stotras, aaratis, bhajans and live devotional channels." />
      <PageHero
        overline={pick("संग्रह", "Sangraha")}
        deva="ध्वनि संग्रह"
        en={pick("ध्वनि संग्रह", "Sacred Audio Library")}
        tagline={pick("मंत्र, स्तोत्र, आरती, भजन एवं लाइव चैनल — सुनते रहें, पढ़ते रहें।", "Mantras, stotras, aaratis, bhajans and live channels — keep listening as you browse.")}
      />

      <div className="mx-auto max-w-6xl px-4 pb-24 md:px-8">
        {/* Live channels */}
        {playableChannels.length > 0 && (
          <section className="mt-10">
            <div className="mb-3 flex items-center gap-2 px-1">
              <Radio className="h-5 w-5 text-red-600" />
              <h2 className="font-serif-latin text-xl text-ink md:text-2xl">{pick("लाइव चैनल", "Live Channels")}</h2>
            </div>
            <div className="flex snap-x gap-4 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {playableChannels.map((c) => {
                const active = liveChannel && liveChannel.title === c.title;
                const color = c.color || accentFor(c.title);
                return (
                  <button key={c.id} data-testid={`live-channel-${c.id}`} onClick={() => playChannel(c)}
                    className={`group relative flex h-32 w-56 shrink-0 snap-start flex-col justify-between overflow-hidden rounded-xl p-4 text-left text-white transition-transform duration-300 hover:-translate-y-1 ${active ? "ring-2 ring-white ring-offset-2 ring-offset-[var(--bg)]" : ""}`}
                    style={{ background: `linear-gradient(135deg, ${color}, ${color}bb)` }}>
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1 rounded-sm bg-black/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"><Radio className="h-3 w-3" /> <T>Live</T></span>
                      <span className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase">{c.lang}</span>
                    </div>
                    <div>
                      <p className="font-serif-latin text-lg leading-tight">{pick(c.title_hi || c.title, c.title)}</p>
                      <p className="mt-0.5 flex items-center gap-1 text-xs text-white/85"><Play className="h-3 w-3" fill="currentColor" /> {c.tracks.length} {pick("ट्रैक", "tracks")} · {active ? pick("चल रहा", "On air") : pick("सुनें", "Tune in")}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Sections */}
        {sections.map((s) => (
          <Row key={s.id} title={pick(s.title_hi || s.title, s.title)} subtitle={s.deity || ""}>
            {s.tracks.map((a) => <TrackCard key={a.id} a={a} pick={pick} />)}
          </Row>
        ))}

        {data && sections.length === 0 && playableChannels.length === 0 && (
          <p className="py-24 text-center text-ink-3">{pick("जल्द ही ऑडियो जोड़े जाएँगे।", "Audio is being added soon.")}</p>
        )}
      </div>
    </div>
  );
}
